import { useQuery } from "@apollo/client";
import { memo } from "react";

import { GET_PROVIDER_DOC_CONTENT } from "../gql";
import { ProviderDocTreeNode } from "./helpers";
import ResourceContent from "./ResourceContent";
import ProviderVersionDocsSkeleton from "./Skeleton";

type DocContentProps = {
  selectedDoc: ProviderDocTreeNode | undefined;
  providerId: string;
  versionNumber: string;
};

const DocContent = ({ selectedDoc, providerId, versionNumber }: DocContentProps) => {
  // Skip query if no doc selected or if doc has markdown already
  const shouldSkip = !selectedDoc || !selectedDoc.key || selectedDoc.markdown !== null;

  const { data, loading } = useQuery(GET_PROVIDER_DOC_CONTENT, {
    variables: { id: providerId, versionNumber, key: selectedDoc?.key || "" },
    skip: shouldSkip,
    fetchPolicy: "cache-first",
  });

  // If doc has markdown already (e.g., overview), use it
  if (selectedDoc?.markdown !== null && selectedDoc?.markdown !== undefined) {
    return <ResourceContent name={selectedDoc.name} markdown={selectedDoc.markdown} />;
  }

  // Show loading skeleton
  if (loading) {
    return <ProviderVersionDocsSkeleton />;
  }

  // Show content if we have data
  if (data?.terraformProvider?.version?.docs?.content && selectedDoc) {
    return (
      <ResourceContent
        name={selectedDoc.name}
        markdown={data.terraformProvider.version.docs.content}
      />
    );
  }

  // Show placeholder if no doc selected
  if (!selectedDoc) {
    return (
      <ResourceContent
        name="Documentation"
        markdown="Select a resource from the sidebar to view its documentation."
      />
    );
  }

  // Fallback for no content
  return <ResourceContent name={selectedDoc.name} markdown="No content available." />;
};

export default memo(DocContent);
